//Login.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from '../utils/api';
import { motion } from "framer-motion";
import "./Login.css";



const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Encode credentials for Basic Auth
      const credentials = btoa(`${username}:${password}`);
      const testApi = api.create({
        //baseURL: 'https://pranayuvbackendfinal-production.up.railway.app',
         baseURL: 'http://localhost:8080/',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/json',
        },
      });

      // Store credentials
      localStorage.setItem('credentials', credentials);
      localStorage.setItem('username', username);

      // Try admin endpoint
      try {
        await testApi.get('/admin/getmessages');
        localStorage.setItem('role', 'ROLE_ADMIN');
        setMessage('Login successful!');
        navigate('/admin-dashboard');
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Validate user credentials with public endpoint
          await testApi.get('/pran/home');
          localStorage.setItem('role', 'ROLE_USER');
          setMessage('Login successful!');
          navigate('/user-dashboard');
        } else {
          throw error;
        }
      }
    } catch (error) {
      setMessage(`Login failed: ${error.response?.status ? 'Invalid credentials' : 'Server error'}`);
      localStorage.removeItem('credentials');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
    }
  };

  return (
    <div className="login-container-2">
      {/* Animated Background Elements */}
      <div className="background-design-2">
        <div className="shape-2 shape-21"></div>
        <div className="shape-2 shape-22"></div>
        <div className="shape-2 shape-23"></div>
      </div>

      <div className="grid-container-2">
        {/* Hero Section */}
        <motion.section
          className="welcome-section-2"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="welcome-overlay-2"></div>
          <div className="hero-background-2"></div>
          <div className="hero-gradient-2"></div>

          <motion.div
            className="welcome-content-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="hero-icon-2">
              <svg
                className="heart-icon-2"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <h1 className="welcome-title-2">HealthConnect</h1>
            <p className="welcome-subtitle-2">
              Your trusted healthcare management platform. Secure, reliable, and
              innovative.
            </p>

            <div className="feature-icons-2">
              <div className="feature-item-2">
                <div className="feature-icon-2">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                  </svg>
                </div>
                <span>Secure</span>
              </div>
              <div className="feature-item-2">
                <div className="feature-icon-2">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.5 2c-4.69 0-8.5 3.81-8.5 8.5s3.81 8.5 8.5 8.5S20 15.19 20 10.5 16.19 2 11.5 2zM17 9h-2.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5H17c.28 0 .5.22.5.5S17.28 9 17 9zm-6 0H8.5c-.28 0-.5-.22-.5-.5s.22-.5.5-.5H11c.28 0 .5.22.5.5S11.28 9 11 9z" />
                  </svg>
                </div>
                <span>Professional</span>
              </div>
              <div className="feature-item-2">
                <div className="feature-icon-2">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                <span>Caring</span>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* Login Form Section */}
        <motion.section
          className="login-section-2"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="container-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="login-form-wrapper-2"
            >
              <div className="login-header-2">
                <h2 className="login-title-2">Welcome Back</h2>
                <p className="login-subtitle-2">
                  Sign in to access your healthcare dashboard
                </p>
              </div>

              <div className="login-card-2">
                <div className="card-header-2">
                  <h3 className="card-title-2">Login</h3>
                  <p className="card-description-2">
                    Enter your credentials to continue
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="login-form-2">
                  <div className="form-group-2">
                    <label htmlFor="username" className="form-label-2">
                      Username
                    </label>
                    <div className="input-container-2">
                      <svg
                        className="input-icon-2"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                      <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="form-input-2"
                        placeholder="Enter your username"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group-2">
                    <label htmlFor="password" className="form-label-2">
                      Password
                    </label>
                    <div className="input-container-2">
                      <svg
                        className="input-icon-2"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM15.1 8H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />
                      </svg>
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input-2"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="password-toggle-2"
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="login-button-2"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing In..." : "Sign In"}
                  </button>

                  {message && (
                    <p
                      className={`message-2 ${
                        message.includes("failed")
                          ? "error-message-2"
                          : "success-message-2"
                      }`}
                    >
                      {message}
                    </p>
                  )}

                  <div className="form-links-2">
                    <Link to="/forgot-password" className="forgot-link-2">
                      Forgot your password?
                    </Link>

                    <div className="signup-section-2">
                      <span>Don't have an account?</span>
                      <Link to="/signup" className="signup-link-2">
                        Sign up
                      </Link>
                    </div>
                  </div>
                </form>
              </div>

              <div className="footer-text-2">
                <p>
                  By signing in, you agree to our{" "}
                  <Link to="/terms" className="footer-link-2">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="footer-link-2">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Login;